import { useMemo } from "react";
import { useMutation, useQuery } from "react-query";
import dataProvider from "@podravka/data-provider";
import dayjs from "dayjs";
import PropTypes from "prop-types";

import { useOneElastic, useUser } from "@hmn/coolinarika-web-core/hooks";
import { globalSettings } from "@hmn/coolinarika-web-core/settings";

const { apiBaseUrl } = globalSettings;

const TAXONOMY_MAP = {
    sex: {
        taxonomy: "protected-coolinarika-user-sex",
        bag: "coolinarika-user-sex",
    },
    country: {
        taxonomy: "coolinarika-countries",
        bag: "coolinarika-user-country",
    },
};

const selectionQueries = [
    { index: "podravkaio-coolinarika-combined" },
    {
        query: {
            bool: {
                must: [
                    {
                        term: {
                            "taxonomy.name": {
                                value: "protected-coolinarika-user-sex",
                            },
                        },
                    },
                ],
                must_not: [
                    {
                        term: {
                            slug: {
                                value: "sex-undefined",
                            },
                        },
                    },
                ],
            },
        },
        sort: {
            tree_left: {
                order: "asc",
            },
        },
    },
    { index: "podravkaio-coolinarika-combined" },
    {
        query: {
            term: {
                "taxonomy.name": {
                    value: "coolinarika-countries",
                },
            },
        },
        size: 250,
        sort: {
            tree_left: {
                order: "asc",
            },
        },
    },
    "", // so that queries are terminated by new line (_msearch requirement)
]
    .map((line) => (line ? JSON.stringify(line) : line))
    .join("\r\n");

const useBasic = ({ successAction }) => {
    const { id: userId } = useUser();

    const { data: userData, refetch } = useOneElastic({
        resource: "moj-profil",
        id: userId,
        enabled: !!userId,
    });

    const { profile: userDataMain = {} } = useUser();

    const { data: selectionsData } = useQuery(
        ["multi", "user", "selections"],
        async () => {
            const response = await fetch(
                `${apiBaseUrl}/elastic/proxy/podravkaio-coolinarika-combined/_msearch`,
                {
                    method: "POST",
                    body: selectionQueries,
                    headers: {
                        "content-type": "application/x-ndjson",
                    },
                },
            );
            const data = await response.json();

            return data;
        },
        {
            enabled: !!apiBaseUrl,
            staleTime: Infinity,
            cacheTime: Infinity,
        },
    );

    const selections = useMemo(() => {
        if (!selectionsData) {
            return [];
        }

        const { responses = [] } = selectionsData;

        return responses.map((response) => {
            if (!Array.isArray(response?.hits?.hits)) {
                return [];
            }

            return response?.hits?.hits?.map(({ _source }) => ({
                ..._source,
            }));
        });
    }, [selectionsData]);

    const initialValues = useMemo(
        () => ({
            extended_attributes: {
                firstName: userDataMain?.extended_attributes?.first_name || "",
                lastName: userDataMain?.extended_attributes?.last_name || "",
                address: userDataMain?.extended_attributes?.address || "",
                zip: userData?.extended_attributes?.zip || "",
                phoneNumber: userDataMain?.extended_attributes?.phone_number || "",
                city: userData?.extended_attributes?.city || "",
                birthday: userData?.extended_attributes?.birthday
                    ? dayjs(userData.extended_attributes.birthday).format("YYYY-MM-DD")
                    : "",
                displayFullName: userData?.extended_attributes?.displayFullName || false,
            },
            taxons: {
                sex: userData?.taxons?.["coolinarika-user-sex"]?.[0]?.id || "",
                country: userData?.taxons?.["coolinarika-user-country"]?.[0]?.id || "",
            },
        }),
        [userData],
    );

    const { mutate, isLoading } = useMutation(
        async (values) => {
            if (isLoading) {
                return undefined;
            }

            const { data: newData } = await dataProvider.update("users", {
                id: userId,
                data: {
                    extended_attributes: {
                        ...values?.extended_attributes,
                        birthday: values?.extended_attributes?.birthday
                            ? dayjs(values.birthday).format("YYYY-MM-DDTHH:mm:ssZ")
                            : null,
                        profile_information_completed: true,
                    },
                },
            });

            Object.entries(values?.taxons).forEach(async ([key, value]) => {
                const taxonomy = TAXONOMY_MAP[key]?.taxonomy;
                const bag = TAXONOMY_MAP[key]?.bag;
                const taxon = userData?.taxons?.[TAXONOMY_MAP[key].bag]?.find(
                    (tax) => tax?.id === initialValues?.taxons?.[key],
                );

                // if  initial value === selected value or  no initial value and no selected value => do nothing

                // if !!initial value and !!value && initial value !==  selected value => delete then create
                if (
                    !!initialValues?.taxons?.[key] &&
                    !!value &&
                    initialValues?.taxons?.[key] !== value
                ) {
                    await dataProvider.delete(`taxonomies/${taxonomy}`, {
                        entity: "users",
                        entityId: userId,
                        bag,
                        id: taxon?.content_taxon_id,
                    });

                    await dataProvider.create(`taxonomies/${taxonomy}`, {
                        entity: "users",
                        entityId: userId,
                        bag,
                        data: { taxon: `api/taxonomies/${taxonomy}/taxons/${value}` },
                    });
                }

                // if  no initial value  and selected value => create
                if (!initialValues?.taxons?.[key] && !!value) {
                    await dataProvider.create(`taxonomies/${taxonomy}`, {
                        entity: "users",
                        entityId: userId,
                        bag,
                        data: { taxon: `api/taxonomies/${taxonomy}/taxons/${value}` },
                    });
                }
            });

            return newData;
        },
        {
            onSuccess: () => {
                successAction();
                setTimeout(() => {
                    refetch();
                }, 1500); // Give Elastic time to update
            },
        },
    );
    return { selections, initialValues, mutate, userId, isLoading };
};

useBasic.propTypes = {
    customOnSuccess: PropTypes.func.isRequired,
};

export default useBasic;
